import { IconType } from 'react-icons';
import {
  FaBuilding,
  FaStore,
  FaCloudSun,
  FaChartBar,
  FaSolarPanel,
  FaBolt,
} from 'react-icons/fa';

export interface CaseStudy {
  id: string;
  title: string;
  industry: string;
  icon: IconType;
  challenge: string;
  solution: string;
  technologies: string[];
  results: string[];
}

export const caseStudies: CaseStudy[] = [
  {
    id: 'procurement-portal',
    title: 'Government Procurement Portal',
    industry: 'Public Sector',
    icon: FaBuilding,
    challenge:
      'A public agency needed to modernize their supplier management system, replacing legacy processes with a digital-first approach that could handle thousands of suppliers and complex procurement workflows.',
    solution:
      'Built a comprehensive React Admin interface backed by Hasura GraphQL, featuring real-time data synchronization, role-based access control, and automated document management. The system integrates with existing government databases while providing a modern, intuitive user experience.',
    technologies: [
      'React Admin',
      'Hasura GraphQL',
      'PostgreSQL',
      'TypeScript',
      'Docker',
    ],
    results: [
      'Reduced supplier onboarding time by 70%',
      'Automated compliance checking for 5,000+ suppliers',
      'Real-time procurement status tracking',
    ],
  },
  {
    id: 'retail-optimization',
    title: 'Retail Network Optimization',
    industry: 'Retail & Gaming',
    icon: FaStore,
    challenge:
      'A nationwide retail network needed to optimize store placement and predict performance across thousands of locations, taking into account demographics, competition, and foot traffic patterns.',
    solution:
      'Developed a machine learning platform combining geospatial analysis with predictive modeling. Interactive Leaflet maps allow executives to visualize network performance, simulate new locations, and identify underperforming sites.',
    technologies: [
      'Python',
      'scikit-learn',
      'Leaflet',
      'PostGIS',
      'React',
      'FastAPI',
    ],
    results: [
      'Identified 15% revenue improvement opportunities',
      'ML model predicts location performance with 85% accuracy',
      'Interactive mapping for 4,000+ retail points',
    ],
  },
  {
    id: 'weather-pipeline',
    title: 'Weather Data Pipeline',
    industry: 'Agriculture & Energy',
    icon: FaCloudSun,
    challenge:
      'Agricultural and energy companies required reliable access to historical and real-time weather data across multiple geographic regions for planning and risk assessment.',
    solution:
      'Built an automated ETL pipeline that ingests weather data from multiple sources, normalizes formats, and maintains a 15+ year historical database. The system handles daily updates, data quality checks, and provides API access for downstream applications.',
    technologies: [
      'Python',
      'PostgreSQL',
      'Apache Airflow',
      'TimescaleDB',
      'REST APIs',
    ],
    results: [
      '15+ years of historical data processed',
      'Daily automated updates with 99.9% reliability',
      'Sub-second query response for historical analysis',
    ],
  },
  {
    id: 'price-intelligence',
    title: 'Market Price Intelligence',
    industry: 'E-commerce',
    icon: FaChartBar,
    challenge:
      'A market research firm needed to track pricing across multiple online sources to provide competitive intelligence to their retail clients.',
    solution:
      'Created a distributed web scraping system that collects pricing data from dozens of sources daily. The platform includes data normalization, trend analysis, and alerting when significant price changes occur.',
    technologies: [
      'Python',
      'Scrapy',
      'PostgreSQL',
      'Redis',
      'React Dashboard',
    ],
    results: [
      'Tracks 50,000+ products daily',
      'Multi-source data aggregation and normalization',
      'Automated anomaly detection and alerting',
    ],
  },
  {
    id: 'grid-analysis',
    title: 'Power Grid Analysis Platform',
    industry: 'Energy',
    icon: FaSolarPanel,
    challenge:
      'An energy company needed to analyze grid capacity and identify optimal locations for solar installations across a complex distribution network.',
    solution:
      'Developed a geospatial analysis platform that processes grid topology data, calculates capacity constraints, and ranks potential solar sites. The system visualizes results on interactive maps with detailed technical metrics.',
    technologies: [
      'Python',
      'GeoPandas',
      'PostGIS',
      'Deck.gl',
      'React',
      'NetworkX',
    ],
    results: [
      'Analyzed 10,000+ grid connection points',
      'Reduced site assessment time from weeks to hours',
      'Identified 200+ high-potential solar locations',
    ],
  },
  {
    id: 'energy-dashboard',
    title: 'Energy Market Dashboard',
    industry: 'Utilities & Regulation',
    icon: FaBolt,
    challenge:
      'A regulatory body required a comprehensive dashboard to monitor energy market dynamics, track compliance metrics, and generate reports for stakeholders.',
    solution:
      'Built an executive dashboard using Highmaps for geographic visualization and custom charts for time-series analysis. The platform aggregates data from multiple market participants and automates regulatory reporting.',
    technologies: [
      'React',
      'Highcharts/Highmaps',
      'Node.js',
      'PostgreSQL',
      'PDF Generation',
    ],
    results: [
      'Real-time market monitoring across all regions',
      'Automated monthly regulatory reports',
      'Interactive drill-down from national to local level',
    ],
  },
];
